/**
 * SourceRecordBadge — T12.D.cm.
 *
 * Badge wyświetlany na rekordzie który został utworzony przez cross-module
 * action (np. „Wystaw fakturę z serwisu"). Pokazuje skąd przyszedł rekord
 * + linkuje do oryginału.
 *
 * Pre-condition: record.source_module_id i record.source_record_id NIE NULL.
 * Komponent sam fetchuje nazwę modułu źródła (lekka query, cached przez react-query).
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GitBranch } from 'lucide-react';

import { api } from '@/lib/api';

interface SourceRecordBadgeProps {
  sourceModuleId: string;
  sourceRecordId: string;
}

interface ModuleSummary {
  id: string;
  name: string;
  slug: string;
  color: string;
}

export function SourceRecordBadge({ sourceModuleId, sourceRecordId }: SourceRecordBadgeProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch lekki — same metadane modułu źródła
  const moduleQuery = useQuery({
    queryKey: ['modules', sourceModuleId, 'summary'],
    queryFn: async () => {
      const res = await api.get<{ module: ModuleSummary }>(`/modules/${sourceModuleId}`);
      return res.data.module;
    },
    enabled: !!sourceModuleId,
    staleTime: 5 * 60 * 1000,
  });

  const mod = moduleQuery.data;
  if (!mod) {
    // Brak danych (loading albo moduł usunięty) — nie renderuj
    return null;
  }

  const handleClick = () => {
    navigate(`/modules/${mod.slug}/${sourceRecordId}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition hover:opacity-80"
      style={{ backgroundColor: `${mod.color}22`, color: mod.color }}
      title={t('tenant.modules.records.detail.sourceClickHint')}
      data-testid="record-source-badge"
    >
      <GitBranch className="h-3 w-3" />
      {t('tenant.modules.records.detail.createdFrom', { module: mod.name })}
    </button>
  );
}
